import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6e288rv53dtlm7j8u20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="h-20 flex items-center justify-between px-12">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/15748bbc-8677-44d2-bf02-39f61c2cd3da.png" alt="MENOM d.o.o." className="h-10 w-10 object-contain" />
            <h1 className="text-2xl font-medium text-gray-900">MENOM d.o.o.</h1>
          </div>
          <div className="flex items-center gap-10">
            <button onClick={() => scrollToSection('about')} className="text-sm uppercase tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-sm uppercase tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-sm uppercase tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-sm uppercase tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-purple-50 via-blue-50 to-white pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        <div className="container mx-auto px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
          <div className="space-y-6">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium">iOS Development Experts</p>
            <h2 className="text-7xl font-thin leading-tight">
              Crafting Premium<br />
              <span className="italic font-serif">iOS Experiences</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
              VPN and Cleaner solutions engineered for modern mobile security
            </p>
          </div>
          <div className="relative flex justify-center">
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/d142c98352f871e0f909439acc86c110.jpg" 
                alt="iOS App Interface" 
                className="w-full max-w-md object-contain transform rotate-3 hover:rotate-0 transition-transform duration-500"
              />
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-purple-200 rounded-full opacity-40 blur-3xl"></div>
              <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-blue-200 rounded-full opacity-40 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-slate-50">
        <div className="container mx-auto px-12">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium">About MENOM d.o.o.</p>
            <p className="text-3xl leading-relaxed text-gray-800 font-light">
              MENOM d.o.o. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. 🛡️
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="container mx-auto px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4">Our Products</p>
            <h3 className="text-5xl font-light text-gray-900">Premium iOS Solutions</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Featured VPN Card */}
            <div className="lg:col-span-2 bg-white rounded-3xl shadow-lg overflow-hidden group hover:shadow-2xl transition-shadow duration-300">
              <div className="relative h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=Premium%20VPN%20application%20interface%20on%20iPhone%20screen%20showing%20secure%20connection%20animation%20with%20shield%20icon%20modern%20gradient%20design%20purple%20and%20blue%20colors%20encryption%20visualization%20clean%20UI%20professional%20app%20screenshot%20high%20quality&width=800&height=600&seq=vpn-product-001&orientation=landscape" 
                  alt="VPN Protection" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h4 className="text-4xl font-bold mb-3">VPN Protection</h4>
                  <p className="text-lg text-white/90">Enterprise-grade encryption for complete privacy</p>
                </div>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden group hover:shadow-2xl transition-shadow duration-300">
              <div className="relative h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20Cleaner%20app%20interface%20showing%20storage%20optimization%20UI%20with%20circular%20progress%20indicators%20clean%20modern%20design%20light%20background%20file%20management%20visualization%20professional%20mobile%20app%20screenshot%20minimalist%20style&width=500&height=600&seq=cleaner-product-001&orientation=portrait" 
                  alt="Cleaner" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute top-8 left-0 right-0 text-center">
                  <h4 className="text-3xl font-bold text-white">Cleaner</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-slate-50">
        <div className="container mx-auto px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=iPhone%20displaying%20modern%20iOS%20app%20with%20glassmorphism%20effects%20frosted%20glass%20UI%20elements%20elegant%20interface%20design%20premium%20mobile%20application%20soft%20shadows%20clean%20white%20background%20professional%20product%20photography%20high%20quality&width=600&height=800&seq=features-phone-001&orientation=portrait" 
                alt="iOS Technology" 
                className="w-full max-w-md mx-auto object-contain shadow-2xl rounded-3xl"
              />
            </div>
            <div className="space-y-12">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4">Technology</p>
                <h3 className="text-6xl font-bold text-gray-900 mb-8">Built for iOS</h3>
              </div>
              
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="text-2xl font-medium text-gray-900 mb-2">Advanced Security</h4>
                  <p className="text-base text-gray-600 leading-relaxed">Military-grade encryption protocols protect your data and ensure complete privacy across all your iOS devices.</p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-speed-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="text-2xl font-medium text-gray-900 mb-2">Optimized Performance</h4>
                  <p className="text-base text-gray-600 leading-relaxed">Native iOS development ensures seamless integration and lightning-fast performance on all Apple devices.</p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-green-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-smile-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="text-2xl font-medium text-gray-900 mb-2">Intuitive Design</h4>
                  <p className="text-base text-gray-600 leading-relaxed">Beautiful interfaces designed following Apple's Human Interface Guidelines for the best user experience.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-slate-100">
        <div className="container mx-auto px-12">
          <div className="bg-white rounded-2xl shadow-lg p-12 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Brand Column */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <img src="https://public.readdy.ai/ai/img_res/15748bbc-8677-44d2-bf02-39f61c2cd3da.png" alt="MENOM d.o.o." className="h-8 w-8 object-contain" />
                  <h4 className="text-xl font-medium text-gray-900">MENOM d.o.o.</h4>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Developing modern applications for the iOS platform to enhance user experience and improve mobile device security.
                </p>
              </div>

              {/* Location Column */}
              <div className="space-y-4">
                <h5 className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-4">Location</h5>
                <div className="space-y-2 text-base text-gray-700">
                  <div className="flex items-start gap-2">
                    <i className="ri-map-pin-line text-gray-500 mt-1"></i>
                    <div>
                      <p>M TITA BB</p>
                      <p>City Tesanj</p>
                      <p>Zip 74260</p>
                      <p>Country Bosnia and Herzegovina</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Column */}
              <div className="space-y-4">
                <h5 className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-4">Contact</h5>
                <div className="space-y-3 text-base text-gray-700">
                  <div className="flex items-center gap-3">
                    <i className="ri-mail-line text-gray-500"></i>
                    <a href="mailto:info@menomdoo.com" className="hover:text-purple-600 transition-colors cursor-pointer">info@menomdoo.com</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-phone-line text-gray-500"></i>
                    <a href="tel:+17744692416" className="hover:text-purple-600 transition-colors cursor-pointer">+1 7744692416</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="mt-12 pt-12 border-t border-gray-200">
              <h5 className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-6">Send us a message</h5>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    required 
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Your message (max 500 characters)"
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-medium cursor-pointer whitespace-nowrap text-sm"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <div className="border-t border-gray-300 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-600">
                <p>© 2025 MENOM d.o.o.</p>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600 transition-colors cursor-pointer">Website Builder</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
